
import React from 'react';
import PageSection from './ui/page-section';
import SectionHeader from './ui/section-header';
import { Check, BarChart, Users, Brain, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const PersonaAI: React.FC = () => {
  const offerings = [
    {
      title: "AI-Driven Focus Groups",
      description: "Simulated consumer discussions, tested for accuracy against real-world participants."
    },
    {
      title: "Custom AI Personas",
      description: "AI models trained to reflect your target audience for continuous insights."
    },
    {
      title: "Consumer Behavior Prediction",
      description: "AI models that anticipate trends before they happen."
    },
    {
      title: "Qualitative + Quantitative Research",
      description: "AI translates deep human conversations into data-driven insights."
    }
  ];

  const audiences = [
    {
      icon: <BarChart className="h-5 w-5 text-accent" />,
      title: "Brands & Enterprises",
      description: "Understand customers better, refine messaging, and optimize product-market fit."
    },
    {
      icon: <Brain className="h-5 w-5 text-accent" />,
      title: "Startups & Web3 Projects",
      description: "Rapid market validation without the high costs."
    },
    {
      icon: <Users className="h-5 w-5 text-accent" />,
      title: "Agencies & Researchers",
      description: "Scale qualitative research faster than ever before."
    }
  ];

  return (
    <PageSection id="persona-ai" dark>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="text-center lg:text-left">
          {/* PersonaAI Logo and Title with improved contrast */}
          <div className="flex flex-col items-center lg:items-start mb-6">
            <div className="relative mb-4 animate-fade-in">
              <div className="absolute inset-0 bg-primary/30 rounded-full blur-xl"></div>
              <div className="absolute inset-0 bg-accent/20 rounded-full blur-md"></div>
              <img 
                src="/lovable-uploads/dc674fb4-34ce-4621-b95e-0dd37770aca6.png" 
                alt="PersonaAI Logo" 
                className="h-24 relative z-10"
              />
            </div>
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent/20 text-white mb-4 animate-fade-in">
              <span className="text-sm font-medium">PersonaAI – AI-Powered Market Research</span>
            </div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fade-in">
            AI Research That <span className="text-gradient">Thinks Like Your Customers</span>
          </h2>
          <p className="text-lg text-white/80 mb-8 animate-fade-in delay-200 max-w-2xl mx-auto lg:mx-0">
            Traditional market research is slow and expensive. PersonaAI uses advanced AI personas—trained to think and behave like real consumers—to deliver deep insights at speed and scale.
          </p>
          
          <div className="animate-fade-in delay-400">
            <Button size="lg" className="btn-hover-effect group bg-accent hover:bg-accent/90 text-white" asChild>
              <a href="http://personaresearch.ai" target="_blank" rel="noopener noreferrer">
                Get a Custom AI Research Solution
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-4 text-white/90">What We Offer:</h3>
            {offerings.map((offering, index) => (
              <div 
                key={index} 
                className="flex items-start mb-4 animate-fade-in-right"
                style={{ animationDelay: `${index * 100 + 200}ms` }}
              >
                <div className="flex-shrink-0 mr-3">
                  <div className="p-1 rounded-full bg-accent/20 text-accent">
                    <Check className="h-5 w-5" />
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-white">{offering.title}</h4>
                  <p className="text-white/70 text-sm">{offering.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div>
            <h3 className="text-xl font-bold mb-4 text-white/90">Who It's For:</h3>
            <div className="space-y-4">
              {audiences.map((audience, index) => (
                <div 
                  key={index} 
                  className="flex items-start animate-fade-in-right"
                  style={{ animationDelay: `${(index + offerings.length) * 100 + 200}ms` }}
                >
                  <div className="flex-shrink-0 mr-3">
                    <div className="p-1 rounded-full bg-accent/20 text-accent">
                      {audience.icon}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{audience.title}</h4>
                    <p className="text-white/70 text-sm">{audience.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default PersonaAI;
